/**
 * SigningPage — PUBLIC, no auth required.
 * Route: /sign/:token
 *
 * Customer-facing contract signing page. Fetches GET /api/sign/:token (no auth).
 * Signs via POST /api/sign/:token { email, confirmName }.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { PublicContractData } from "./contractTypes";
import { renderMarkdown } from "./markdownUtils";

// ── Helpers ──────────────────────────────────────────────────────────────

function fmtCurrency(val: number) {
  return val.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function fmtDate(d?: string) {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

// Simple confetti via CSS animation
function Confetti() {
  const colors = ["#22c55e", "#3b82f6", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4"];
  const pieces = Array.from({ length: 60 }, (_, i) => i);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-40">
      {pieces.map((i) => {
        const color = colors[i % colors.length];
        const left = Math.random() * 100;
        const delay = Math.random() * 1.5;
        const duration = 2 + Math.random() * 1.5;
        const size = 8 + Math.random() * 8;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${left}%`,
              top: "-20px",
              width: size,
              height: size,
              backgroundColor: color,
              borderRadius: i % 3 === 0 ? "50%" : "2px",
              animation: `confetti-fall ${duration}s ${delay}s ease-in forwards`,
              transform: `rotate(${Math.random() * 360}deg)`,
            }}
          />
        );
      })}
      <style>{`
        @keyframes confetti-fall {
          to { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────

type PageState = "loading" | "ready" | "already_signed" | "void" | "not_found" | "signed";
type PaymentStep = "none" | "prompt" | "loading" | "done";

// ── SigningPage ───────────────────────────────────────────────────────────

export default function SigningPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>("loading");
  const [contract, setContract] = useState<PublicContractData | null>(null);
  const [email, setEmail] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [agreedToEsign, setAgreedToEsign] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [paymentStep, setPaymentStep] = useState<PaymentStep>("none");
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);

  const apiBase = ((import.meta as Record<string, any>).env?.VITE_MESSAGING_API_URL as string) ?? "";

  const load = useCallback(async () => {
    if (!token) { setState("not_found"); return; }
    try {
      const res = await fetch(`${apiBase}/api/sign/${token}`);
      if (!res.ok) { setState("not_found"); return; }
      const data: PublicContractData = await res.json();
      setContract(data);
      setEmail(data.customerEmail ?? "");
      if (data.status === "void") setState("void");
      else if (data.status === "signed") setState("already_signed");
      else setState("ready");
    } catch {
      setState("not_found");
    }
  }, [token, apiBase]);

  useEffect(() => { load(); }, [load]);

  // Parse line items safely
  const lineItems: Array<{ description: string; qty: number; unitPrice: number }> = (() => {
    try {
      if (contract?.lineItemsJson) {
        const p = JSON.parse(contract.lineItemsJson);
        return Array.isArray(p) ? p : [];
      }
    } catch {
      // ignore
    }
    return [];
  })();

  async function handleSign(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !confirmName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/sign/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, confirmName, agreedToElectronicSignature: true }),
      });
      if (res.ok) {
        if (contract?.setupFee && contract.setupFee > 0) {
          setPaymentStep("prompt");
        }
        setState("signed");
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 4000);
      } else {
        const txt = await res.text();
        setError(txt || "Failed to sign. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePayNow() {
    setPaymentStep("loading");
    try {
      const res = await fetch(`${apiBase}/api/sign/${token}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.checkoutUrl) {
          window.open(data.checkoutUrl, "_blank");
          setPaymentUrl(data.checkoutUrl);
          setPaymentStep("done");
        } else {
          setPaymentStep("prompt");
          setError(data.error || "Payment link unavailable. Contact support.");
        }
      } else {
        setPaymentStep("prompt");
        setError("Unable to create payment link. Please contact support.");
      }
    } catch {
      setPaymentStep("prompt");
      setError("Network error. Please try again.");
    }
  }

  // ── States ──────────────────────────────────────────────────────────

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-400 text-sm">Loading contract...</div>
      </div>
    );
  }

  if (state === "not_found") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-5xl mb-4">&#128269;</div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Contract Not Found</h1>
          <p className="text-slate-500">This signing link may be invalid or expired.</p>
        </div>
      </div>
    );
  }

  if (state === "void") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-5xl mb-4">&#128683;</div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Contract No Longer Active</h1>
          <p className="text-slate-500">This contract has been voided.</p>
        </div>
      </div>
    );
  }

  if (state === "already_signed") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-3xl mx-auto mb-4">
            &#10003;
          </div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Already Signed</h1>
          <p className="text-slate-600">
            This document was signed by{" "}
            <strong>{contract?.signedByEmail || contract?.customerEmail}</strong>{" "}
            on {fmtDate(contract?.signedAt)}.
          </p>
        </div>
      </div>
    );
  }

  if (state === "signed") {
    return (
      <>
        {showConfetti && <Confetti />}
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
          <div className="text-center max-w-md w-full">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center text-4xl mx-auto mb-5 animate-bounce">
              &#10003;
            </div>
            <h1 className="text-2xl font-bold text-green-700 mb-2">Signed!</h1>
            <p className="text-slate-600 text-sm leading-relaxed">
              Thank you, <strong>{confirmName}</strong>. Your signature has been recorded.
              You will receive a confirmation email at <strong>{email}</strong> shortly.
            </p>

            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 text-left">
                {error}
              </div>
            )}

            {paymentStep === "prompt" && (
              <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6 text-left">
                <h2 className="text-base font-semibold text-slate-800 mb-2">Complete Your Payment</h2>
                <p className="text-slate-600 text-sm mb-4">
                  Your contract is signed. Next, please submit the setup fee of{" "}
                  <strong>{fmtCurrency(contract!.setupFee!)}</strong> to activate your account.
                </p>
                <button
                  onClick={handlePayNow}
                  className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition-colors"
                >
                  Pay Now
                </button>
              </div>
            )}

            {paymentStep === "loading" && (
              <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6 text-left">
                <h2 className="text-base font-semibold text-slate-800 mb-2">Complete Your Payment</h2>
                <p className="text-slate-600 text-sm mb-4">
                  Your contract is signed. Next, please submit the setup fee of{" "}
                  <strong>{fmtCurrency(contract!.setupFee!)}</strong> to activate your account.
                </p>
                <button
                  disabled
                  className="w-full py-3 bg-green-600 text-white font-bold rounded-xl opacity-50"
                >
                  Loading payment...
                </button>
              </div>
            )}

            {paymentStep === "done" && (
              <div className="mt-6 bg-green-50 rounded-2xl border border-green-200 p-6 text-center">
                <div className="text-2xl mb-2">&#10003;</div>
                <p className="text-green-800 font-semibold">Payment link opened!</p>
                <p className="text-green-700 text-sm mt-1">
                  Complete your payment in the new tab. You'll receive an email confirmation.
                </p>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // ── Main signing view ────────────────────────────────────────────────

  const isAmendment = !!(contract?.amendmentId);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-white font-bold text-sm">
              V
            </div>
            <span className="font-semibold text-slate-800 text-sm">Velaro</span>
          </div>
          <div className="text-sm text-slate-500">
            {isAmendment ? "Amendment for Signature" : "Contract for Signature"}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Title + customer block */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <div className="mb-5">
            {isAmendment && (
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200 mb-3">
                Amendment to Original Contract
              </div>
            )}
            <h1 className="text-2xl font-bold text-slate-900">
              {isAmendment ? (contract?.amendmentTitle ?? "Amendment") : contract?.title}
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Prepared for <strong>{contract?.customerName}</strong>
              {contract?.customerCompany ? <>, {contract.customerCompany}</> : null}
            </p>
          </div>

          {/* Key terms grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-slate-50 rounded-xl p-4 text-sm">
            <div>
              <div className="text-slate-400 text-xs font-medium uppercase tracking-wide">Monthly Value</div>
              <div className="font-bold text-slate-900 text-base mt-0.5">
                {fmtCurrency(contract?.totalMonthlyValue ?? 0)}
              </div>
            </div>
            <div>
              <div className="text-slate-400 text-xs font-medium uppercase tracking-wide">Net Terms</div>
              <div className="font-semibold text-slate-700 mt-0.5">{contract?.netTerms}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs font-medium uppercase tracking-wide">Start Date</div>
              <div className="font-semibold text-slate-700 mt-0.5">{fmtDate(contract?.serviceStartDate)}</div>
            </div>
            {contract?.serviceEndDate ? (
              <div>
                <div className="text-slate-400 text-xs font-medium uppercase tracking-wide">End Date</div>
                <div className="font-semibold text-slate-700 mt-0.5">{fmtDate(contract.serviceEndDate)}</div>
              </div>
            ) : null}
            {(contract?.setupFee ?? 0) > 0 ? (
              <div>
                <div className="text-slate-400 text-xs font-medium uppercase tracking-wide">Setup Fee</div>
                <div className="font-semibold text-slate-700 mt-0.5">{fmtCurrency(contract!.setupFee!)}</div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Line items */}
        {lineItems.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
            <h2 className="text-base font-semibold text-slate-800 mb-4">Services &amp; Pricing</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left pb-2 font-medium text-slate-500">Description</th>
                  <th className="text-right pb-2 font-medium text-slate-500 w-16">Qty</th>
                  <th className="text-right pb-2 font-medium text-slate-500 w-28">Unit Price</th>
                  <th className="text-right pb-2 font-medium text-slate-500 w-24">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {lineItems.map((row, i) => (
                  <tr key={i}>
                    <td className="py-3 text-slate-700">{row.description}</td>
                    <td className="py-3 text-right text-slate-600">{row.qty}</td>
                    <td className="py-3 text-right text-slate-600">{fmtCurrency(row.unitPrice)}</td>
                    <td className="py-3 text-right font-semibold text-slate-800">
                      {fmtCurrency(row.qty * row.unitPrice)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-slate-200">
                <tr>
                  <td colSpan={3} className="pt-3 text-right font-semibold text-slate-700">
                    Monthly Total
                  </td>
                  <td className="pt-3 text-right font-bold text-lg text-slate-900">
                    {fmtCurrency(lineItems.reduce((s, r) => s + r.qty * r.unitPrice, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Contract / Amendment body */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <h2 className="text-base font-semibold text-slate-800 mb-4">
            {isAmendment ? "Amendment Terms" : "Agreement Terms"}
          </h2>
          <div
            className="text-sm text-slate-700 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: renderMarkdown(
                isAmendment ? (contract?.amendmentBody ?? "") : (contract?.body ?? "")
              ),
            }}
          />
        </div>

        {/* Signature section */}
        <div className="bg-white rounded-2xl border-2 border-cornflower-blue-200 p-6 sm:p-8">
          <h2 className="text-base font-semibold text-slate-800 mb-1">Your Signature</h2>
          <p className="text-slate-500 text-sm mb-5">
            I, <strong>{contract?.customerName}</strong>, agree to the terms above.
          </p>

          <form onSubmit={handleSign} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Full Name (type to confirm)
              </label>
              <input
                type="text"
                required
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                placeholder="Type your full legal name"
              />
            </div>

            {/* ESIGN consent checkbox — required before signing */}
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <input
                id="esign-consent"
                type="checkbox"
                checked={agreedToEsign}
                onChange={(e) => setAgreedToEsign(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-green-600 flex-shrink-0"
              />
              <label htmlFor="esign-consent" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                I consent to the use of electronic records and signatures. I understand that by clicking
                "I Accept and Sign" below, I am providing a legally binding electronic signature
                equivalent to a handwritten signature, in accordance with the Electronic Signatures
                in Global and National Commerce Act (ESIGN) and Uniform Electronic Transactions Act (UETA).
              </label>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !email.trim() || !confirmName.trim() || !agreedToEsign}
              className="w-full py-4 bg-green-500 hover:bg-green-600 text-white font-bold text-base rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {submitting ? "Signing..." : "I Accept and Sign"}
            </button>

            <p className="text-xs text-slate-400 text-center leading-relaxed">
              By clicking above, you agree to these terms. Your IP address and timestamp will be
              recorded as your electronic signature in accordance with the Electronic Signatures
              in Global and National Commerce Act (E-SIGN).
            </p>
          </form>
        </div>

        <div className="text-center text-xs text-slate-400 pb-8">
          Powered by Velaro &middot; Secure Electronic Signature
        </div>
      </div>
    </div>
  );
}
